import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { describeError } from "@/lib/api";
import AuthShell from "@/components/layout/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { User, Building2 } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "", firm_name: "" });
  const [accountType, setAccountType] = useState("individual");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, account_type: accountType });
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      setError(describeError(err, "Unable to create account. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const types = [
    { value: "individual", label: "Individual Advocate", icon: User },
    { value: "law_firm", label: "Law Firm", icon: Building2 },
  ];

  return (
    <AuthShell title="Create your account" subtitle="Start managing your legal practice in minutes.">
      <form onSubmit={submit} className="space-y-4">
        {error && <div data-testid="register-error" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">{error}</div>}

        <div>
          <Label className="mb-2 block">Account type</Label>
          <div className="grid grid-cols-2 gap-3">
            {types.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                data-testid={`account-type-${value}`}
                onClick={() => setAccountType(value)}
                className={cn(
                  "flex flex-col items-center gap-2 rounded-md border p-3 text-sm font-medium transition-colors",
                  accountType === value ? "border-navy bg-navy/5 text-navy" : "border-border text-muted-foreground hover:border-navy/40"
                )}
              >
                <Icon className="h-5 w-5" strokeWidth={1.75} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {accountType === "law_firm" && (
          <div className="space-y-1.5">
            <Label htmlFor="firm">Law firm name</Label>
            <Input id="firm" data-testid="register-firm-input" value={form.firm_name} onChange={set("firm_name")} placeholder="Sharma & Associates" required />
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Adv. Full Name" required />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" data-testid="register-email-input" value={form.email} onChange={set("email")} placeholder="you@chambers.in" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mobile">Mobile</Label>
            <Input id="mobile" data-testid="register-mobile-input" value={form.mobile} onChange={set("mobile")} placeholder="98765 43210" required />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" data-testid="register-password-input" value={form.password} onChange={set("password")} placeholder="Minimum 8 characters" minLength={8} required />
        </div>

        <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="h-11 w-full bg-navy text-white hover:bg-navy-light">
          {loading ? "Creating account…" : "Create account"}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-navy hover:text-gold" data-testid="go-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
